import React from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { LC } from './landingTokens';

// ─── Synthesis-node mark ──────────────────────────────────────────────────────
// Three connected nodes; the bottom node is always the accent. Two color
// variants: onDark (mint strokes/nodes, used on evergreen surfaces) and onLight
// (evergreen strokes/nodes, used on paper). `animated` enables the staggered
// pulse used only during stack generation; halted under prefers-reduced-motion.

const msPulse = keyframes`
  0%, 100% { transform: scale(.74); opacity: .4; }
  50%      { transform: scale(1.16); opacity: 1; }
`;

// One-time entrance: connector lines draw in, then nodes settle (scale/fade).
const drawLine = keyframes`from { stroke-dashoffset: 48; } to { stroke-dashoffset: 0; }`;
const popNode = keyframes`
  0%   { transform: scale(0); opacity: 0; }
  70%  { transform: scale(1.12); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const pulseSx = (delay) => ({
  transformBox: 'fill-box',
  transformOrigin: 'center',
  animation: `${msPulse} 1.6s ease ${delay} infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

const introLineSx = {
  strokeDasharray: 48,
  animation: `${drawLine} 620ms cubic-bezier(.4,.7,.3,1) both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none', strokeDasharray: 'none' },
};
const introNodeSx = (delay) => ({
  transformBox: 'fill-box',
  transformOrigin: 'center',
  animation: `${popNode} 420ms cubic-bezier(.3,1.3,.5,1) ${delay} both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

export default function Mark({ size = 26, variant = 'onDark', strokeWidth, animated = false, intro = false, sx }) {
  const stroke = variant === 'onLight' ? LC.evergreen : LC.mint;
  const node = stroke;          // dim nodes match the stroke color
  const litNode = LC.accent;    // bottom node always accent
  const sw = strokeWidth ?? Math.max(2.4, (3.5 * 26) / size); // visually consistent across sizes

  const nodeSx = (delay) => (animated ? pulseSx(delay) : intro ? introNodeSx(`${380 + parseFloat(delay) * 1000}ms`) : undefined);
  const lineSx = intro ? introLineSx : undefined;

  return (
    <Box
      component="svg"
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
      sx={{ display: 'block', flex: 'none', ...sx }}
    >
      <Box component="line" x1="16" y1="20" x2="50" y2="14" stroke={stroke} strokeWidth={sw} sx={lineSx} />
      <Box component="line" x1="16" y1="20" x2="40" y2="46" stroke={stroke} strokeWidth={sw} sx={lineSx} />
      <Box component="line" x1="50" y1="14" x2="40" y2="46" stroke={stroke} strokeWidth={sw} sx={lineSx} />
      <Box component="circle" cx="16" cy="20" r="7" fill={node} sx={nodeSx('0s')} />
      <Box component="circle" cx="50" cy="14" r="7" fill={node} sx={nodeSx('.22s')} />
      <Box component="circle" cx="40" cy="46" r="10" fill={litNode} sx={nodeSx('.44s')} />
    </Box>
  );
}
